/**
 * registry.ts — Modul-Registry aller aktiven Kommandos (docs/30 §9.3 / §9.2).
 *
 * EINE Wahrheit, welche Kommandos gerade tastbar sind. Komponenten melden ihre
 * Kommandos beim Mount an (useCommandScope) und beim Unmount ab — die run-
 * Closures leben in der aufrufenden Komponente, die Registry haelt nur die
 * Verweise. Der Dispatcher (useHotkeys) fragt ueber matchCommand ab, ob eine
 * getippte Tastenkombination ein aktives Kommando trifft.
 *
 * Bewusst getrennt vom keydown-Listener: die Registry weiss NICHT, wie Tasten
 * normalisiert werden — sie vergleicht nur fertige KeySpecs. So bleibt sie auch
 * fuer die spaetere Kommando-Palette (E4b) als reine Kommando-Liste nutzbar.
 */

import { useEffect, useRef } from 'react';
import type { CommandContext, KeySpec } from './useHotkeys';

export type CommandScope = 'global' | 'shell' | 'chat' | 'list' | 'editor';

export interface Command {
  id: string;
  titleKey: string;
  /** Tastenkombinationen als fertige KeySpecs (Muster: 'mod+0', 'mod+alt+KeyI'). */
  keys?: KeySpec[];
  group?: string;
  scope?: CommandScope;
  /** Aktiv/sichtbar nur, wenn erfuellt. Ohne when() ist das Kommando immer aktiv. */
  when?(ctx: CommandContext): boolean;
  run(ctx: CommandContext): void | Promise<void>;
  /** true = feuert nicht bei e.repeat (Festhalten schaltet nicht durch). */
  noRepeat?: boolean;
}

// ---------------------------------------------------------------------------
// Modul-State: aktive Kommandos (Reihenfolge der Registrierung bleibt erhalten).
// ---------------------------------------------------------------------------

const commands: Command[] = [];

/**
 * Registriert ein Kommando und liefert einen idempotenten unregister zurueck
 * (Splice per Identitaet — mehrfaches Aufrufen ist sicher).
 */
export function registerCommand(cmd: Command): () => void {
  commands.push(cmd);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const index = commands.lastIndexOf(cmd);
    if (index >= 0) commands.splice(index, 1);
  };
}

/** Registriert mehrere Kommandos auf einmal; unregister raeumt alle ab. */
export function registerCommands(cmds: Command[]): () => void {
  const unregisters = cmds.map(registerCommand);
  return () => {
    for (const unregister of unregisters) unregister();
  };
}

/**
 * React-Hook: registriert die uebergebenen Kommandos beim Mount, meldet sie beim
 * Unmount ab — als PROXY-Kommandos, deren run/when zur FEUERZEIT aus einer bei
 * jedem Render aktualisierten Ref gelesen werden (Latest-Ref-Muster).
 *
 * WICHTIG (E4a-Verify-Befund): Die fruehere Fassung registrierte die Objekte des
 * ERSTEN Renders und behielt deren run-Closures — ⌘0 blieb dadurch an die
 * Mount-Groessenklasse gebunden (useNavShape.toggle ist useCallback([size])) und
 * schaltete nach iPad-Rotation die Rail-Pref der FALSCHEN Klasse. Die Proxys
 * halten Registrierung UND Frische auseinander: registriert wird einmal pro
 * Scope, gelesen wird immer der letzte Render-Stand.
 *
 * Der scope-Parameter ueberschreibt cmd.scope NICHT, sondern dient als
 * gemeinsame Absteckung des Aufrufers (dokumentarisch + Re-Registrierungs-Key).
 */
export function useCommandScope(scope: CommandScope, cmds: Command[]): void {
  const latest = useRef(cmds);
  latest.current = cmds;
  useEffect(() => {
    const proxies = latest.current.map((_: Command, index: number): Command => {
      const read = () => latest.current[index];
      const first = latest.current[index];
      return {
        // Identitaet/Anzeige duerfen vom ersten Render stammen (id/keys/group
        // sind statisch definiert); Verhalten kommt aus der Ref.
        ...first,
        scope: first.scope ?? scope,
        get keys() {
          return read()?.keys ?? first.keys;
        },
        when: (ctx) => {
          const cur = read();
          return cur?.when ? cur.when(ctx) : true;
        },
        run: (ctx) => read()?.run(ctx),
      };
    });
    return registerCommands(proxies);
    // Registrierung nur bei Scope-Wechsel; Frische liefert die latest-Ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);
}

// ---------------------------------------------------------------------------
// Abfrage
// ---------------------------------------------------------------------------

/**
 * Findet ein aktives Kommando, dessen keys den gegebenen spec enthalten und
 * dessen when(ctx) (falls vorhanden) erfuellt ist. Bei mehreren Treffern
 * gewinnt der zuletzt Registrierte (spezifischere Route sticht die globale
 * Absteckung), deshalb von hinten gesucht.
 */
export function matchCommand(spec: KeySpec, ctx: CommandContext): Command | undefined {
  for (let i = commands.length - 1; i >= 0; i -= 1) {
    const cmd = commands[i];
    if (!cmd.keys?.includes(spec)) continue;
    if (cmd.when && !cmd.when(ctx)) continue;
    return cmd;
  }
  return undefined;
}

/** Liefert eine Momentaufnahme aller aktiven Kommandos (fuer Tests/Palette). */
export function getRegisteredCommands(): readonly Command[] {
  return commands.slice();
}
